import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { apiGetCurrentQuiz, apiAnswerQuiz, apiSkipQuiz } from '../../api/quizApi';
import { updateCardAccount } from '../../store/slices/authSlice';
import type { RootState } from '../../types/store/state';
import type { QuizQuestion, QuizAnswerResult } from '../../types/models/quiz';

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase =
  | { name: 'loading' }
  | { name: 'cooldown'; availableAt: Date }
  | { name: 'ready'; attemptId: number; question: QuizQuestion }
  | { name: 'question'; attemptId: number; question: QuizQuestion; timeLeft: number }
  | { name: 'result'; result: QuizAnswerResult; selectedChoiceId: number; question: QuizQuestion }
  | { name: 'skipped'; availableAt: Date }
  | { name: 'error' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCountdown = (target: Date): string => {
  const diff = Math.max(0, Math.floor((target.getTime() - Date.now()) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const shrink = keyframes`
  from { width: 100%; }
  to   { width: 0%; }
`;

const Container = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const WidgetTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 215, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const QuestionText = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.45;
`;

const TimerBar = styled.div<{ $seconds: number; $paused: boolean }>`
  height: 3px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    background-color: ${({ $seconds }) => ($seconds <= 3 ? '#f87171' : $seconds <= 6 ? '#fbbf24' : 'gold')};
    animation: ${({ $paused }) => ($paused ? 'none' : shrink)} ${({ $seconds }) => $seconds}s linear forwards;
  }
`;

const TimerLabel = styled.span<{ $low: boolean }>`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $low }) => ($low ? '#f87171' : 'rgba(255,255,255,0.45)')};
  align-self: flex-end;
`;

const ChoicesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChoiceBtn = styled.button<{ $state: 'default' | 'correct' | 'wrong' | 'dimmed' }>`
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
  text-align: left;
  cursor: ${({ $state }) => ($state === 'default' ? 'pointer' : 'default')};
  transition: background-color 0.15s, border-color 0.15s;

  ${({ $state }) => {
    if ($state === 'correct') return `
      background-color: rgba(40, 167, 69, 0.15);
      border: 1px solid rgba(40, 167, 69, 0.6);
      color: #5adf7a;
      font-weight: 700;
    `;
    if ($state === 'wrong') return `
      background-color: rgba(248, 113, 113, 0.12);
      border: 1px solid rgba(248, 113, 113, 0.4);
      color: #f87171;
    `;
    if ($state === 'dimmed') return `
      background-color: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.3);
    `;
    return `
      background-color: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.85);
      &:hover { background-color: rgba(255, 215, 0, 0.08); border-color: rgba(255, 215, 0, 0.35); color: #fff; }
    `;
  }}
`;

const ResultBox = styled.div<{ $correct: boolean }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  background-color: ${({ $correct }) => ($correct ? 'rgba(40, 167, 69, 0.1)' : 'rgba(248, 113, 113, 0.08)')};
  border: 1px solid ${({ $correct }) => ($correct ? 'rgba(40, 167, 69, 0.4)' : 'rgba(248, 113, 113, 0.3)')};
  color: ${({ $correct }) => ($correct ? '#5adf7a' : '#f87171')};
`;

const CooldownBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CooldownLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CooldownValue = styled.span`
  font-size: 20px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.6);
`;

const StatusText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  margin: 0;
  text-align: center;
`;

const StartBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 8px 0;
`;

const StartHint = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
  text-align: center;
  line-height: 1.4;
`;

const StartBtn = styled.button`
  padding: 10px 32px;
  border-radius: 6px;
  border: none;
  background-color: #38bdf8;
  color: #000;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;
  &:hover { background-color: #7dd3fc; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const TIMER_SECONDS = 15;

interface QuizWidgetProps {
  compact?: boolean;
}

const QuizWidget: React.FC<QuizWidgetProps> = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const [phase, setPhase] = useState<Phase>({ name: 'loading' });
  const [cooldownDisplay, setCooldownDisplay] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const skipCalledRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const fetchQuiz = useCallback(async () => {
    setPhase({ name: 'loading' });
    skipCalledRef.current = false;
    try {
      const res = await apiGetCurrentQuiz();
      if (res.status === 'cooldown') {
        setPhase({ name: 'cooldown', availableAt: new Date(res.available_at) });
      } else {
        setPhase({
          name: 'ready',
          attemptId: res.data.attempt_id,
          question: res.data.question,
        });
      }
    } catch {
      setPhase({ name: 'error' });
    }
  }, []);

  useEffect(() => {
    fetchQuiz();
    return () => clearTimer();
  }, [fetchQuiz]);

  // Countdown display for cooldown/skipped phases
  useEffect(() => {
    if (phase.name !== 'cooldown' && phase.name !== 'skipped') return;
    const target = phase.availableAt;
    const update = () => setCooldownDisplay(formatCountdown(target));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Question timer
  useEffect(() => {
    if (phase.name !== 'question') return;
    clearTimer();

    timerRef.current = setInterval(() => {
      setPhase((prev) => {
        if (prev.name !== 'question') return prev;
        const next = prev.timeLeft - 1;
        if (next <= 0) return { ...prev, timeLeft: 0 };
        return { ...prev, timeLeft: next };
      });
    }, 1000);

    return clearTimer;
  }, [phase.name === 'question' ? (phase as any).attemptId : null]);

  // Auto-skip when timer hits 0
  useEffect(() => {
    if (phase.name !== 'question' || phase.timeLeft > 0 || skipCalledRef.current) return;
    skipCalledRef.current = true;
    clearTimer();
    apiSkipQuiz(phase.attemptId)
      .then((res) => setPhase({ name: 'skipped', availableAt: new Date(res.available_at) }))
      .catch(() => setPhase({ name: 'error' }));
  }, [phase]);

  const handleStart = () => {
    if (phase.name !== 'ready') return;
    setPhase({ name: 'question', attemptId: phase.attemptId, question: phase.question, timeLeft: TIMER_SECONDS });
  };

  const handleAnswer = async (choiceId: number) => {
    if (phase.name !== 'question') return;
    clearTimer();
    const { attemptId, question } = phase;
    try {
      const result = await apiAnswerQuiz(attemptId, choiceId);
      if (result.credits_earned > 0 && user?.card_account) {
        dispatch(updateCardAccount({ ...user.card_account, credits: result.credits_remaining }));
      }
      setPhase({ name: 'result', result, selectedChoiceId: choiceId, question });
    } catch {
      setPhase({ name: 'error' });
    }
  };

  const choiceState = (
    phase: Phase & { name: 'result' },
    choiceId: number
  ): 'correct' | 'wrong' | 'dimmed' => {
    if (choiceId === phase.result.correct_choice_id) return 'correct';
    if (choiceId === phase.selectedChoiceId) return 'wrong';
    return 'dimmed';
  };

  // ── Render ──

  if (phase.name === 'loading') {
    return (
      <Container>
        <WidgetTitle>Quiz del momento</WidgetTitle>
        <StatusText>Caricamento...</StatusText>
      </Container>
    );
  }

  if (phase.name === 'error') {
    return (
      <Container>
        <WidgetTitle>Quiz del momento</WidgetTitle>
        <StatusText>Errore nel caricamento. Riprova più tardi.</StatusText>
      </Container>
    );
  }

  if (phase.name === 'cooldown') {
    return (
      <Container>
        <WidgetTitle>Quiz del momento</WidgetTitle>
        <CooldownBox>
          <CooldownLabel>Prossimo quiz tra</CooldownLabel>
          <CooldownValue>{cooldownDisplay}</CooldownValue>
        </CooldownBox>
      </Container>
    );
  }

  if (phase.name === 'ready') {
    return (
      <Container>
        <WidgetTitle>Quiz del momento</WidgetTitle>
        <StartBox>
          <StartHint>
            Una domanda ti aspetta. Avrai <strong style={{ color: '#fff' }}>15 secondi</strong> per rispondere — il timer parte non appena premi il bottone.
          </StartHint>
          <StartBtn onClick={handleStart}>Inizia il Quiz</StartBtn>
        </StartBox>
      </Container>
    );
  }

  if (phase.name === 'skipped') {
    return (
      <Container>
        <WidgetTitle>Quiz del momento</WidgetTitle>
        <ResultBox $correct={false}>Tempo scaduto! Nessun credito.</ResultBox>
        <CooldownBox>
          <CooldownLabel>Prossimo quiz tra</CooldownLabel>
          <CooldownValue>{cooldownDisplay}</CooldownValue>
        </CooldownBox>
      </Container>
    );
  }

  if (phase.name === 'result') {
    const { result, question } = phase;
    return (
      <Container>
        <WidgetTitle>Quiz del momento</WidgetTitle>
        <QuestionText>{question.question}</QuestionText>
        <ChoicesGrid>
          {question.choices.map((c) => (
            <ChoiceBtn key={c.id} $state={choiceState(phase, c.id)}>
              {c.text}
            </ChoiceBtn>
          ))}
        </ChoicesGrid>
        <ResultBox $correct={result.is_correct}>
          {result.is_correct
            ? `Corretto! +${result.credits_earned} crediti`
            : result.credits_earned > 0
              ? `Risposta errata. +${result.credits_earned} crediti.`
              : 'Risposta errata. Riprova al prossimo quiz.'}
        </ResultBox>
      </Container>
    );
  }

  // phase === 'question'
  return (
    <Container>
      <WidgetTitle>Quiz del momento</WidgetTitle>
      <QuestionText>{phase.question.question}</QuestionText>
      <TimerBar $seconds={phase.timeLeft} $paused={false} />
      <TimerLabel $low={phase.timeLeft <= 3}>{phase.timeLeft}s</TimerLabel>
      <ChoicesGrid>
        {phase.question.choices.map((c) => (
          <ChoiceBtn
            key={c.id}
            $state="default"
            onClick={() => handleAnswer(c.id)}
          >
            {c.text}
          </ChoiceBtn>
        ))}
      </ChoicesGrid>
    </Container>
  );
};

export default QuizWidget;
